import React, { useEffect, useState } from "react";
import { Alert, Chip, CircularProgress, Tab, Tabs } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { AppButton } from "../../components/ui";
import AccountsAPI from "../../services/AccountsAPI";
import { formatSubscriptionDate, formatSubscriptionLabel, getSubscriptionState } from "../../utility/subscriptionAccess";
import { BILLING_PLANS, PLAN_COMPARISON, formatPlanPrice, getAccountPlanName, matchesPurchasedPlan } from "../../utility/billingPlans";
import "./UpgradePage.scss";

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const UpgradePage = () => {
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [checkoutError, setCheckoutError] = useState("");
  const [redirectingPlan, setRedirectingPlan] = useState(null);
  const [activationState, setActivationState] = useState("idle");
  const [activatedSubscription, setActivatedSubscription] = useState(null);
  const [individualInterval, setIndividualInterval] = useState("year");
  const { refreshAuthSession, userId } = auth;
  const checkoutStatus = searchParams.get("checkout");
  const purchasedType = searchParams.get("purchaseType") || "classroom_license";
  const purchasedInterval = searchParams.get("billingInterval") || "year";
  const currentSubscription = getSubscriptionState({
    status: auth.subscriptionStatus,
    source: auth.subscriptionSource,
    expirationDate: auth.subscriptionEndsAt,
    renews: auth.subscriptionRenews,
    cancelAtPeriodEnd: auth.subscriptionCancelAtPeriodEnd,
  });
  const hasPaidPlan = currentSubscription.isSubscribed || Boolean(activatedSubscription);

  useEffect(() => {
    if (checkoutStatus !== "success" || !userId) return undefined;
    let cancelled = false;
    let timer;
    let attempt = 0;
    setActivationState("pending");
    setActivatedSubscription(null);

    const poll = async () => {
      attempt += 1;
      try {
        const response = await AccountsAPI.get(`/users/${userId}`, { requiresAuth: true });
        if (cancelled) return;
        const user = getApiResponseData(response);
        const subscription = getSubscriptionState({ ...user?.subscription, status: user?.subscription?.type });
        if (subscription.isSubscribed && matchesPurchasedPlan(user?.subscription, purchasedType, purchasedInterval)) {
          setActivatedSubscription(user.subscription);
          await refreshAuthSession();
          if (!cancelled) setActivationState("complete");
          return;
        }
      } catch (error) {
        // The signed-in account, not the return URL, confirms activation.
      }
      if (cancelled) return;
      if (attempt >= 12) setActivationState("delayed");
      else timer = window.setTimeout(poll, 2500);
    };
    void poll();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [checkoutStatus, purchasedType, purchasedInterval, refreshAuthSession, userId]);

  const handleUpgrade = async (plan, product) => {
    if (hasPaidPlan || redirectingPlan || product?.available !== true || checkoutStatus === "success") return;
    setCheckoutError("");
    setRedirectingPlan(`${plan.purchaseType}:${plan.billingInterval}`);
    try {
      const response = await AccountsAPI.post("/users/upgrade", {
        purchaseType: plan.purchaseType,
        billingInterval: plan.billingInterval,
      }, { requiresAuth: true });
      const payload = getApiResponseData(response);
      if (!payload?.url) throw new Error("Checkout could not be opened. Please try again.");
      window.location.assign(payload.url);
    } catch (error) {
      setCheckoutError(error?.response?.data?.message || "Unable to start checkout right now. Please try again.");
      setRedirectingPlan(null);
      if (error?.response?.status === 409) await refreshAuthSession();
    }
  };

  return (
    <main className="upgradePage">
      <section className="upgradePage__hero">
        <div className="upgradePage__eyebrow">10k Science</div>
        <div className="upgradePage__headlineRow">
          <div>
            <h1 className="upgradePage__title">{hasPaidPlan ? "Your subscription" : "Choose your plan"}</h1>
            <p className="upgradePage__subtitle">
              {hasPaidPlan
                ? "Your plan is already active. Manage renewal and view billing history from your account."
                : "Explore on your own, teach your classes, or bring web and VR to your entire site. Compare what’s included below."}
            </p>
          </div>
          <div className="upgradePage__statusCard">
            <div className="upgradePage__statusLabel">Current account</div>
            <Chip className="upgradePage__statusChip" color={currentSubscription.hasActiveSubscription ? "success" : "info"}
              label={formatSubscriptionLabel(currentSubscription.status, currentSubscription.hasActiveSubscription)} />
            <div className="upgradePage__statusMeta">{getAccountPlanName(auth)}</div>
            {currentSubscription.hasActiveSubscription && auth.subscriptionEndsAt && <div className="upgradePage__statusMeta">
              {currentSubscription.renews ? "Renews on " : "Access through "}{formatSubscriptionDate(auth.subscriptionEndsAt)}
            </div>}
          </div>
        </div>
        {checkoutStatus === "cancelled" && <Alert severity="info">Checkout was cancelled. Your account is unchanged.</Alert>}
        {checkoutStatus === "success" && activationState === "pending" && <Alert severity="info">We&apos;re checking your purchase and refreshing your access.</Alert>}
        {checkoutStatus === "success" && activationState === "complete" && <Alert severity="success">Your purchased plan is active.</Alert>}
        {checkoutStatus === "success" && activationState === "delayed" && <Alert severity="warning">We haven&apos;t confirmed this plan on your account yet. Refresh this page in a few moments. You do not need to purchase again.</Alert>}
        {checkoutError && <Alert severity="error">{checkoutError}</Alert>}
      </section>

      {hasPaidPlan ? (
        <section className="upgradePage__detailsCard">
          <h2 className="upgradePage__detailsTitle">Manage your current plan</h2>
          <p className="upgradePage__detailText">To change plans, contact us for help with your existing subscription.</p>
          <div className="upgradePage__inlineActions">
            <AppButton component={Link} to="/account" kind="primary">Manage Billing</AppButton>
            <AppButton component={Link} to="/" kind="secondary">Back to Home</AppButton>
          </div>
        </section>
      ) : (
        <>
        <section className="upgradePage__body" aria-label="Available plans">
          {PLAN_COMPARISON.map((comparison) => {
            const isIndividual = comparison.purchaseType === "consumer_content";
            const isRecommended = comparison.purchaseType === "classroom_license";
            const includesVr = comparison.purchaseType === "site_license";
            const interval = isIndividual ? individualInterval : "year";
            const plan = BILLING_PLANS.find((option) => option.purchaseType === comparison.purchaseType && option.billingInterval === interval);
            const key = `${plan.purchaseType}:${plan.billingInterval}`;
            const product = auth.billingProducts?.find((item) => item.purchaseType === plan.purchaseType && item.billingInterval === plan.billingInterval);
            const available = product?.available === true && Number.isFinite(product?.amount) && product.currency === "usd";
            return (
              <article className={`upgradePage__planCard${isRecommended ? " upgradePage__planCard--featured" : ""}`} key={plan.purchaseType} aria-labelledby={`plan-${plan.purchaseType}`}>
                <div className="upgradePage__planIntro">
                  <div className="upgradePage__planTitleRow">
                    <h2 className="upgradePage__planName" id={`plan-${plan.purchaseType}`}>{comparison.name}</h2>
                    {includesVr && <span className="upgradePage__platformBadge">Web + VR</span>}
                  </div>
                  <p className="upgradePage__featureDescription">{comparison.description}</p>
                </div>
                <div className="upgradePage__billingChoice">
                  {isIndividual && (
                    <Tabs value={individualInterval} onChange={(_, value) => setIndividualInterval(value)}
                      aria-label="Individual billing period" className="upgradePage__billingTabs" variant="fullWidth">
                      <Tab id="individual-month-tab" aria-controls="individual-price" value="month" label="Monthly" disabled={Boolean(redirectingPlan)} />
                      <Tab id="individual-year-tab" aria-controls="individual-price" value="year" label="Annual" disabled={Boolean(redirectingPlan)} />
                    </Tabs>
                  )}
                </div>
                <div className="upgradePage__priceBlock" {...(isIndividual ? { id: "individual-price", role: "tabpanel", "aria-labelledby": `individual-${individualInterval}-tab` } : {})}>
                  <p className="upgradePage__planPrice">{formatPlanPrice(available ? product.amount : plan.amount, plan.billingInterval)}</p>
                  <p className="upgradePage__featureDescription">Billed {plan.billingInterval === "month" ? "monthly" : "annually"}.</p>
                </div>
                <ul className="upgradePage__comparisonFeatures" aria-label={`${comparison.name} features`}>
                  {comparison.features.map((feature) => (
                    <li className="upgradePage__comparisonFeature" key={feature}>
                      <CheckRoundedIcon aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {!available && <p className="upgradePage__detailText">
                  {auth.isAccountProfileLoading ? "Checking availability…" : "Online checkout is not available for this plan yet."}
                </p>}
                <div className="upgradePage__ctaRow">
                  <AppButton kind={isRecommended ? "primary" : "secondary"} loading={redirectingPlan === key} loadingLabel="Opening checkout…"
                    disabled={!available || Boolean(redirectingPlan) || checkoutStatus === "success"}
                    onClick={() => handleUpgrade(plan, product)}>
                    Choose {plan.name}
                  </AppButton>
                </div>
              </article>
            );
          })}
        </section>
        <section className="upgradePage__explanations" aria-label="Trial and billing details">
          <div>
            <h2 className="upgradePage__explanationTitle">During your 7-day trial</h2>
            <p className="upgradePage__detailText">Try web content, educator analytics, and student join codes for web and VR. VR setup is included. Custom guide creation is available with a paid Classroom plan or Site license.</p>
          </div>
          <div>
            <h2 className="upgradePage__explanationTitle">After your trial</h2>
            <p className="upgradePage__detailText">You can still sign in to your account. Choose a plan to continue accessing content and the tools included with that plan.</p>
          </div>
          <div>
            <h2 className="upgradePage__explanationTitle">Renewal is in your control</h2>
            <p className="upgradePage__detailText">Plans renew automatically at the billing interval you choose. Turn off auto-renew from your account at any time; your access continues through the end of your paid term.</p>
          </div>
        </section>
        </>
      )}
      {activationState === "pending" && <div className="upgradePage__loadingState"><CircularProgress size={20} /><span>Refreshing your account…</span></div>}
      {!hasPaidPlan && <AppButton component={Link} to="/account" kind="ghost">Back to Account</AppButton>}
    </main>
  );
};

export default UpgradePage;
